import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { MagnifyingGlassIcon, FunnelIcon, UsersIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { studentAPI } from '../services/api';

const AVAILABLE_BRANCHES = [
  'Computer Science',
  'Mechanical Engineering',
  'Electronics Engineering',
  'Civil Engineering',
  'Chemical Engineering',
  'Information Technology',
  'Electrical Engineering',
];

const StudentsPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: 'pending',
    branch: '',
    search: '',
    page: 1,
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const queryClient = useQueryClient();

  // Fetch students
  const { data, isLoading, error, refetch } = useQuery(
    ['students', filters],
    () => studentAPI.getStudents({
      ...filters,
      status: filters.status === 'all' ? undefined : filters.status,
      branch: filters.branch || undefined,
    }),
    {
      keepPreviousData: true,
    }
  );

  // Approve student mutation
  const approveMutation = useMutation(studentAPI.approveStudent, {
    onSuccess: () => {
      toast.success('Student approved successfully');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('dashboard');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to approve student');
    },
  });

  // Reject student mutation
  const rejectMutation = useMutation(
    ({ userId, reason }) => studentAPI.rejectStudent(userId, reason),
    {
      onSuccess: () => {
        toast.success('Student rejected successfully');
        queryClient.invalidateQueries('students');
        queryClient.invalidateQueries('dashboard');
        setShowRejectModal(false);
        setSelectedStudent(null);
        setRejectReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject student');
      },
    }
  );

  // Promote to Ambassador mutation
  const promoteAmbassadorMutation = useMutation(studentAPI.promoteToAmbassador, {
    onSuccess: () => {
      toast.success('Student promoted to Ambassador successfully');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('faculty');
      queryClient.invalidateQueries('dashboard');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to promote to ambassador');
    },
  });

  const handleApprove = (studentId) => {
    if (window.confirm('Are you sure you want to approve this student?')) {
      approveMutation.mutate(studentId);
    }
  };

  const handleReject = (student) => {
    setSelectedStudent(student);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    rejectMutation.mutate({
      userId: selectedStudent._id,
      reason: rejectReason,
    });
  };

  const handlePromoteToAmbassador = (userId) => {
    if (window.confirm('Are you sure you want to promote this student to Ambassador? They will get special privileges to manage clubs.')) {
      promoteAmbassadorMutation.mutate(userId);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  if (isLoading) return <LoadingSpinner text="Loading students..." />;

  if (error) {
    console.error('❌ Students Error:', error);
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading students</p>
        <p className="text-sm text-gray-500 mb-4">{error.message}</p>
        <button onClick={() => refetch()} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  const { students = [], pagination = {}, stats = {} } = data?.data?.data || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage student registrations and approvals
          </p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { value: 'all', label: 'All Students', count: data?.data?.data?.stats?.total || 0 },
              { value: 'pending', label: 'Pending Applications', count: data?.data?.data?.stats?.pending || 0 },
              { value: 'approved', label: 'Approved', count: data?.data?.data?.stats?.approved || 0 },
              { value: 'rejected', label: 'Rejected', count: data?.data?.data?.stats?.rejected || 0 },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleFilterChange('status', tab.value)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                  ${filters.status === tab.value
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
                <span className={`ml-2 py-0.5 px-2.5 rounded-full text-xs ${
                  filters.status === tab.value
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Filters */}
        <div className="p-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                className="input pl-10"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>

            {/* Branch Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                className="input pl-10"
                value={filters.branch}
                onChange={(e) => handleFilterChange('branch', e.target.value)}
              >
                <option value="">All Branches</option>
                {AVAILABLE_BRANCHES.map((branch) => (
                  <option key={branch} value={branch}>
                    {branch}
                  </option>
                ))}
              </select>
            </div>

            {/* Reset Filters */}
            <button
              onClick={() => setFilters({ status: 'pending', branch: '', search: '', page: 1 })}
              className="btn-secondary"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* Students List */}
      <div className="card">
        {students.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No students match the current filters.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => (
                  <tr key={student._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-600">
                              {student.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div
                            className="text-sm font-medium text-gray-900 hover:text-primary-600 cursor-pointer"
                            onClick={() => navigate(`/users/${student._id}`)}
                          >
                            {student.name}
                          </div>
                          <div className="text-sm text-gray-500">{student.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="badge badge-blue">{student.branch}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`badge ${
                          student.isVerified
                            ? 'badge-green'
                            : student.isRejected
                            ? 'badge-red'
                            : 'badge-yellow'
                        }`}
                      >
                        {student.isVerified
                          ? 'Approved'
                          : student.isRejected
                          ? 'Rejected'
                          : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(student.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {!student.isVerified && !student.isRejected && (
                        <>
                          <button
                            onClick={() => handleApprove(student._id)}
                            disabled={approveMutation.isLoading}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleReject(student)}
                            disabled={rejectMutation.isLoading}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {student.isVerified && (
                        <button
                          onClick={() => handlePromoteToAmbassador(student._id)}
                          disabled={promoteAmbassadorMutation.isLoading}
                          className="text-purple-600 hover:text-purple-900 disabled:opacity-50"
                        >
                          {promoteAmbassadorMutation.isLoading ? 'Promoting...' : '↑ Promote to Ambassador'}
                        </button>
                      )}
                      {student.isRejected && student.rejectionReason && (
                        <span
                          className="text-gray-600 cursor-help"
                          title={student.rejectionReason}
                        >
                          View Reason
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.current - 1) * 20 + 1} to{' '}
              {Math.min(pagination.current * 20, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={!pagination.hasPrev}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-600">
                Page {pagination.current} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={!pagination.hasNext}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Reject Student Application
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting {selectedStudent?.name}'s application.
              </p>
              <textarea
                className="input h-24 mb-4"
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedStudent(null);
                    setRejectReason('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={rejectMutation.isLoading}
                  className="btn-danger"
                >
                  {rejectMutation.isLoading ? 'Rejecting...' : 'Reject Student'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsPage;